import Game from 'Game';
import { AsyncActionStatus } from 'State/AsyncAction';
import { report } from 'report';
import IPlayerData from 'Player/IPlayerData';
import { Native } from '@sgorg/mobile-dev-tools';

interface IdsForBusiness {
    app: {
        name: string;
        id: string;
    };
    id: string;
    user_id_type: string;
}

export class FacebookNativeHandler {
    private static TAG: string = '[FBNativeSaver]';
    private fetchDataCount: number = 0;
    private saveData: object = {};

    public async load(): Promise<IPlayerData> {
        if (!this.enabled) return null;

        return await this.getDataAsync();
    }

    public async save(data: Partial<IPlayerData>): Promise<void> {
        this.saveData = Object.assign(this.saveData, data);
        if (!Object.keys(data).length) return;

        await this.setDataAsync();
    }

    public get enabled(): boolean {
        return Boolean(
            Game.sdk.getFacebookAuthToken() !== '' &&
                Game.sdk.getFacebookAppScopedId() !== '' &&
                Native.main.hasConnectionToNetwork()
        );
    }

    private async getDataAsync(): Promise<IPlayerData> {
        try {
            const request = await fetch(
                `https://graph.fb.gg/${FacebookNativeHandler.getAppScopedId()}/data?access_token=${Game.sdk.getFacebookAuthToken()!}`
            );
            if (!request.ok && this.fetchDataCount < 9) {
                await new Promise(resolve => setTimeout(resolve, 1500));
                this.fetchDataCount++;
                return this.getDataAsync();
            }
            if (!request.ok) throw new Error(request.statusText);

            const { data } = await request.json();
            const entries = data.map((item: any) => [item.key, item.value]);
            const playerData = Object.fromEntries(entries);

            return playerData as IPlayerData;
        } catch (error) {
            console.warn('[Cloud Data]', 'Failed to getDataAsync()', error.message);
            report('Failed to GET Facebook data', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    private async setDataAsync(): Promise<void> {
        const dataConflictStatus = Game.store.getState().dataConflict.status;
        if (!this.enabled || dataConflictStatus !== AsyncActionStatus.COMPLETE) return;

        try {
            const request = await fetch(
                `https://graph.fb.gg/${FacebookNativeHandler.getAppScopedId()}/data?access_token=${Game.sdk.getFacebookAuthToken()!}`,
                {
                    method: 'POST',
                    headers: { 'Content-type': 'application/json; charset=UTF-8' },
                    body: JSON.stringify({ payload: this.saveData }),
                }
            );
            if (!request.ok) throw new Error(request.statusText);

            // Because Facebook API is inconsistent this could cause an issue were in memory data gets lost.
            this.saveData = {};
            console.log('[FacebookNativeSaver]', 'Successfully saved to Facebook');
        } catch (error) {
            report('Failed to POST Facebook data', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    private static getAppScopedId(): string {
        return Game.sdk.getFacebookAppScopedId();
    }

    public async getPlayerName(): Promise<string> {
        try {
            const request = await fetch(
                `https://graph.fb.gg/v7.0/me?field=name&access_token=${Game.sdk.getFacebookAuthToken()!}`
            );
            if (!request.ok) throw new Error(request.statusText);
            const data = await request.json();

            return data.name;
        } catch (error) {
            report('Failed to GET Facebook playerName', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    public async getFacebookID(): Promise<string> {
        try {
            const request = await fetch(
                `https://graph.fb.gg/${FacebookNativeHandler.getAppScopedId()}/ids_for_business?access_token=${Game.sdk.getFacebookAuthToken()!}`
            );
            if (!request.ok) throw new Error(request.statusText);
            const { data } = await request.json();
            const fbInstantAppData = data.filter((appData: IdsForBusiness) => {
                return appData.app.id === Game.config.appId.toString() && appData.user_id_type === 'instant_game';
            })[0];
            if (!fbInstantAppData) throw new Error('Unable to fetch fbInstant playerId');

            return fbInstantAppData.id;
        } catch (error) {
            report('Failed to GET Facebook playerName', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    public async getAvatar(): Promise<string> {
        try {
            const request = await fetch(
                `https://graph.fb.gg/v12.0/me/picture?access_token=${Game.sdk.getFacebookAuthToken()!}`
            );
            if (!request.ok) throw new Error(request.statusText);
            const { url } = await request;

            return urlToBase64(url);
        } catch (error) {
            report('Failed to GET Facebook avatar', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }
}

const urlToBase64 = (url: string, size: number = 400): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;

        img.onload = (e: Event) => {
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(e.currentTarget as HTMLImageElement, 0, 0, size, size);
            const dataURL = canvas.toDataURL('image/png', 1);

            resolve(dataURL);
        };
        img.onerror = () => {
            reject();
        };
    });
};
